import React from 'react';
import { useAuth, MemberRoleType, UserType } from '../../contexts/AuthContext';

interface RoleGuardProps {
  children: React.ReactNode;
  
  // Role-based access control
  allowedRoles?: MemberRoleType[];
  excludeRoles?: MemberRoleType[];
  allowedUserTypes?: UserType[];
  
  // Permission-based access control
  requireAdmin?: boolean;
  requireReportsAccess?: boolean;
  requireUserManagement?: boolean;
  requireSupplierManagement?: boolean;
  
  // Fallback content when access is denied
  fallback?: React.ReactNode;
  
  // Show loading state while checking permissions
  showLoadingFallback?: boolean;
}

/**
 * RoleGuard component that conditionally renders children based on user roles and permissions
 * 
 * @example
 * // Only show to SST users
 * <RoleGuard allowedRoles={['SST']}>
 *   <AdminPanel />
 * </RoleGuard>
 * 
 * @example
 * // Show to all except AM users
 * <RoleGuard excludeRoles={['AM']}>
 *   <ReportsSection />
 * </RoleGuard>
 * 
 * @example
 * // Show only to users who can manage suppliers
 * <RoleGuard requireSupplierManagement>
 *   <SupplierManagement />
 * </RoleGuard>
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  excludeRoles,
  allowedUserTypes,
  requireAdmin = false,
  requireReportsAccess = false,
  requireUserManagement = false,
  requireSupplierManagement = false,
  fallback = null,
  showLoadingFallback = false
}) => {
  const { user, isLoading, isAuthenticated } = useAuth();

  // Show loading state if requested and auth is loading
  if (isLoading && showLoadingFallback) {
    return <div className="loading-spinner">Loading...</div>;
  }

  // User must be authenticated
  if (!isAuthenticated || !user) {
    return fallback;
  }

  // Check user type restrictions
  if (allowedUserTypes && !allowedUserTypes.includes(user.userType!)) {
    return fallback;
  }

  // For Member users, check role-based restrictions
  if (user.userType === 'Member') {
    const userRole = user.memberRole;
    
    // Check allowed roles
    if (allowedRoles && (!userRole || !allowedRoles.includes(userRole))) {
      return fallback;
    }
    
    // Check excluded roles
    if (excludeRoles && userRole && excludeRoles.includes(userRole)) {
      return fallback;
    }
    
    // Check permission-based restrictions
    if (requireAdmin && !user.isAdmin) {
      return fallback;
    }
    
    if (requireReportsAccess && !user.canAccessReports) {
      return fallback;
    }
    
    if (requireUserManagement && !user.canManageUsers) {
      return fallback;
    }
    
    if (requireSupplierManagement && !user.canManageSuppliers) {
      return fallback;
    }
  }

  // All checks passed, render children
  return <>{children}</>;
};

/**
 * Convenience components for common role checks
 */

// Only for SST (admin) users
export const AdminGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <RoleGuard allowedRoles={['SST']} fallback={fallback}>
    {children}
  </RoleGuard>
);

// For all users except AM
export const ReportGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <RoleGuard requireReportsAccess fallback={fallback}>
    {children}
  </RoleGuard>
);

// Only for Member user types
export const MemberGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <RoleGuard allowedUserTypes={['Member']} fallback={fallback}>
    {children}
  </RoleGuard>
);

// For external suppliers only
export const SupplierGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <RoleGuard allowedUserTypes={['Supplier']} fallback={fallback}>
    {children}
  </RoleGuard>
);

/**
 * Hook to check if current user has specific permissions
 */
export const useHasRole = (roles: MemberRoleType[]): boolean => {
  const { user } = useAuth();
  
  if (!user || user.userType !== 'Member' || !user.memberRole) {
    return false;
  }
  
  return roles.includes(user.memberRole);
};

/**
 * Hook to check if current user can access a feature
 */
export const useCanAccess = (options: {
  roles?: MemberRoleType[];
  excludeRoles?: MemberRoleType[];
  userTypes?: UserType[];
  requireAdmin?: boolean;
  requireReports?: boolean;
}): boolean => {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !user) {
    return false;
  }
  
  // Check user type restrictions
  if (options.userTypes && !options.userTypes.includes(user.userType!)) {
    return false;
  }
  
  // For Member users, check role restrictions
  if (user.userType === 'Member') {
    const userRole = user.memberRole;
    
    if (options.roles && (!userRole || !options.roles.includes(userRole))) {
      return false;
    }
    
    if (options.excludeRoles && userRole && options.excludeRoles.includes(userRole)) {
      return false;
    }
    
    if (options.requireAdmin && !user.isAdmin) {
      return false;
    }
    
    if (options.requireReports && !user.canAccessReports) {
      return false;
    }
  }
  
  return true;
};